import type { Reducer, Effect } from 'umi'
import { history } from 'umi'
import { message } from 'antd'
import {
  getUserInfo,
  sendLoginCode,
  fakeAccountLogin,
  accountLogout,
  sendForgetCode,
  retrievePassword,
  get_wechat_user_info,
  fetchLoginStatus,
  get_wechat_state
} from '@/services/system/account'
import { getURLParams } from '@/utils/utils'
import { getPermissionList } from '@/services/user'

const getRefererFromUrl = () => getURLParams('referer', true) || ''

const resolveReferer = (referer?: string) => {
  if (referer) {
    return referer
  }
  if (window.location.pathname === '/user/login') {
    return getRefererFromUrl()
  }
  const { pathname, search, hash } = window.location
  return `${pathname}${search}${hash}`
}

const redirectAfterLogin = (referer?: string) => {
  const target = resolveReferer(referer)
  history.replace(!!target && target.indexOf('/login') === -1 ? target : '/')
}

export interface StateType {
  bindingState: Object
}

export interface LoginModelType {
  namespace: string
  state: StateType
  effects: {
    fetchLogin: Effect
    fetchLogout: Effect
    fetchLoginStatus: Effect
    fetchSendForgetCode: Effect
    fetchRetrievePassword: Effect
    fetchSendLoginCode: Effect
    fetchWechatLogin: Effect
  }
  reducers: {
    logout: Reducer<StateType>
    setBindingState: Reducer<StateType>
  }
}

const Model: LoginModelType = {
  namespace: 'login',

  state: {
    bindingState: {}
  },

  effects: {
    // 登录
    *fetchLogin({ payload = {} }, { call, put }) {
      const { referer: payloadReferer = '', ...loginPayload } = payload
      const referer = resolveReferer(payloadReferer)
      const res = yield call(fakeAccountLogin, { ...loginPayload, is_app: false })
      if (res.code === 0 && !res.data.is_mobile_verification) {
        window.localStorage.setItem('userName', loginPayload.name)
        // 登录成功后，获取并保存用户信息
        const resUserInfo = yield call(getUserInfo)
        // 新的权限列表接口
        const {
          data: { permission }
        } = yield call(getPermissionList)
        yield put({
          type: 'user/saveCurrentUser',
          payload: { ...resUserInfo.data, permission }
        })

        resUserInfo.code === 0 && redirectAfterLogin(referer)
      }

      return new Promise((resolve, reject) => {
        if (res.code == 0 && !res.data.is_mobile_verification) {
          resolve(res)
        } else {
          reject(res)
        }
      })
    },

    // 微信登录
    *fetchWechatLogin({ payload = {} }, { call, put }) {
      const referer = resolveReferer(payload.referer)
      // 获取并保存用户信息
      const res = yield call(getUserInfo)
      // 新的权限列表接口
      const {
        data: { permission }
      } = yield call(getPermissionList)
      yield put({
        type: 'user/saveCurrentUser',
        payload: { ...res.data, permission }
      })
      res.code === 0 && redirectAfterLogin(referer)
    },

    // 登出
    *fetchLogout({}, { call, put }) {
      const res = yield call(accountLogout)
      if (res.code === 0) {
        yield put({
          type: 'logout'
        })
      }
    },

    // 校验登录状态
    *fetchLoginStatus({ payload = {} }, { call, put }) {
      const referer = resolveReferer(payload.referer)

      // 需要绑定手机号
      const code = getURLParams('code')
      // 微信登录判断
      if (window.location.pathname === '/user/login' && code) {
        const stateRes = yield call(get_wechat_state)
        if (stateRes.code === 0) {
          const response = yield call(get_wechat_user_info, {
            code,
            is_app: false,
            state: stateRes.data.state
          })
          if (response.code === 0) {
            if (response.data.info.user_id) {
              // 获取并保存用户信息
              const res = yield call(getUserInfo)
              // 新的权限列表接口
              const {
                data: { permission }
              } = yield call(getPermissionList)
              yield put({
                type: 'user/saveCurrentUser',
                payload: { ...res.data, permission }
              })
              if (res.code === 0) {
                redirectAfterLogin(referer)
              }
            } else {
              // 需要绑定手机号
              yield put({
                type: 'setBindingState',
                payload: {
                  access_token: response.data.info.access_token,
                  open_id: response.data.info.open_id,
                  referer
                }
              })
            }
          }
        }
        return
      }
      const res = yield call(fetchLoginStatus)
      if (res.code === 0) {
        // 获取用户信息
        const resUserInfo = yield call(getUserInfo)
        // 新的权限列表接口
        const {
          data: { permission }
        } = yield call(getPermissionList)
        yield put({
          type: 'user/saveCurrentUser',
          payload: { ...resUserInfo.data, permission }
        })
        // 已登录状态
        if (window.location.pathname === '/user/login') {
          redirectAfterLogin(referer)
        }

        yield put({
          type: 'dictionary/initDictionary'
        })
        yield put({
          type: 'global/fetchNewsUnreadCount'
        })
        yield put({
          type: 'team/fetchGetTeam'
        })
        yield put({
          type: 'team/fetchGetTeamAll'
        })
        // 当前进跟人(所有正常状态账号列表)
        yield put({
          type: 'deal/fetchProcessor',
          payload: {
            status: 1,
            role_code: '',
            page_size: 9999
          }
        })
      } else {
        yield put({
          type: 'logout',
          payload: {
            referer
          }
        })
      }
    },

    // 忘记密码发送验证码
    *fetchSendForgetCode({ payload }, { call, put }) {
      const res = yield call(sendForgetCode, payload)
      if (res.code === 0) {
        return Promise.resolve()
      }
      return Promise.reject()
    },

    // 找回密码
    *fetchRetrievePassword({ payload }, { call, put }) {
      const res = yield call(retrievePassword, payload)
      if (res.code === 0) {
        message.success('密码修改成功')
        return true
      }
      return false
    },

    // 登录发送验证码
    *fetchSendLoginCode({ payload }, { call, put }) {
      const res = yield call(sendLoginCode, payload)
      if (res.code === 0) {
        return Promise.resolve()
      }
      return Promise.reject()
    }
  },

  reducers: {
    logout(state, { payload = {} }): any {
      const { referer = '' } = payload
      if (window.location.pathname !== '/user/login') {
        history.replace({
          pathname: '/user/login',
          query: referer ? { referer } : {}
        })
      }
      return {
        ...state
      }
    },
    setBindingState(state, { payload = {} }): any {
      return {
        ...state,
        bindingState: payload
      }
    }
  }
}

export default Model
